import { useEffect, useState } from "react";
import { Plus, KeyRound, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { adminUsersApi } from "../../lib/authApi";
import { EmptyUsers } from "../../components/illustrations/EmptyIllustrations";
import { EmptyState } from "../../components/ui/empty-state";
import { GridSkeletonRows } from "../../components/ui/grid-skeleton";

const ROLES = [
  { value: "super_admin", label: "Super admin" },
  { value: "titolare", label: "Titolare" },
  { value: "commercial", label: "Commerciale" },
];

function emptyDraft() {
  return {
    email: "",
    full_name: "",
    role: "commercial",
    avatar_initials: "",
  };
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());

  const load = async () => {
    setLoading(true);
    try {
      const { items } = await adminUsersApi.list();
      setUsers(items);
    } catch (e) {
      toast.error("Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createUser = async () => {
    if (!draft.email || !draft.full_name) {
      toast.error("Email e nome sono obbligatori");
      return;
    }
    try {
      const resp = await adminUsersApi.create(draft);
      toast.success(
        `Creato ${resp.user.email}. Password temporanea: ${resp.temp_password}`,
        { duration: 15000 }
      );
      setDraft(emptyDraft());
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione utente");
    }
  };

  const toggleActive = async (u) => {
    try {
      await adminUsersApi.update(u.id, { active: !u.active });
      toast.success(u.active ? "Utente disattivato" : "Utente attivato");
      await load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password per ${u.email}?`)) return;
    try {
      const resp = await adminUsersApi.resetPassword(u.id);
      toast.success(
        `Password temporanea per ${u.email}: ${resp.temp_password}`,
        { duration: 20000 }
      );
    } catch (e) {
      toast.error("Errore reset");
    }
  };

  const changeRole = async (u, newRole) => {
    try {
      await adminUsersApi.update(u.id, { role: newRole });
      toast.success("Ruolo aggiornato");
      await load();
    } catch (e) {
      toast.error("Errore aggiornamento ruolo");
    }
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1
            className="text-[22px] font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            Gestione utenti
          </h1>
          <p
            className="text-[13px] mt-0.5"
            style={{ color: "var(--text-secondary)" }}
          >
            Operatori H&C e super admin. Gli utenti inattivi non possono
            effettuare login.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(!creating)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] font-medium"
          style={{
            background: "var(--accent-primary)",
            color: "var(--text-on-accent)",
          }}
          data-testid="add-user-btn"
        >
          <Plus className="h-4 w-4" />
          Aggiungi utente
        </button>
      </div>

      {creating && (
        <div
          className="rounded-lg p-4 mb-4 grid grid-cols-4 gap-3"
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border-subtle)",
          }}
          data-testid="create-user-form"
        >
          <input
            placeholder="email@hotcoldsrl.com"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            className="h-9 px-2 rounded text-[13px]"
            style={{ border: "1px solid var(--border-default)" }}
            data-testid="create-email"
          />
          <input
            placeholder="Nome Cognome"
            value={draft.full_name}
            onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
            className="h-9 px-2 rounded text-[13px]"
            style={{ border: "1px solid var(--border-default)" }}
            data-testid="create-name"
          />
          <select
            value={draft.role}
            onChange={(e) => setDraft({ ...draft, role: e.target.value })}
            className="h-9 px-2 rounded text-[13px]"
            style={{ border: "1px solid var(--border-default)" }}
            data-testid="create-role"
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            onClick={createUser}
            className="h-9 rounded-md text-[13px] font-medium"
            style={{
              background: "var(--accent-primary)",
              color: "var(--text-on-accent)",
            }}
            data-testid="create-submit"
          >
            Crea
          </button>
        </div>
      )}

      <div
        className="rounded-lg overflow-hidden"
        style={{
          border: "1px solid var(--border-subtle)",
          background: "var(--bg-surface)",
        }}
      >
        <table className="w-full text-[13px]">
          <thead>
            <tr
              style={{
                background: "var(--bg-subtle)",
                color: "var(--text-secondary)",
              }}
            >
              <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase">
                Nome
              </th>
              <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase">
                Email
              </th>
              <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase">
                Ruolo
              </th>
              <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase">
                Stato
              </th>
              <th className="text-left px-3 py-2.5 text-[11px] font-semibold uppercase">
                Ultimo accesso
              </th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {loading && <GridSkeletonRows cols={6} rows={5} />}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    testid="users-empty"
                    illustration={<EmptyUsers />}
                    title="Nessun utente ancora"
                    description='Aggiungi il primo utente con "Aggiungi utente" in alto.'
                  />
                </td>
              </tr>
            )}
            {!loading &&
              users.map((u) => (
                <tr
                  key={u.id}
                  style={{ borderTop: "1px solid var(--border-subtle)" }}
                  data-testid={`user-row-${u.email}`}
                >
                  <td className="px-3 py-2.5">
                    <span
                      className="inline-flex items-center gap-2"
                      style={{ color: "var(--text-primary)" }}
                    >
                      <span
                        className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold"
                        style={{
                          background: "var(--bg-subtle)",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {u.avatar_initials}
                      </span>
                      {u.full_name}
                    </span>
                  </td>
                  <td
                    className="px-3 py-2.5 font-mono text-[12px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {u.email}
                  </td>
                  <td className="px-3 py-2.5">
                    <select
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value)}
                      className="h-7 px-1.5 rounded text-[12px]"
                      style={{ border: "1px solid var(--border-default)" }}
                      data-testid={`role-${u.email}`}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className="inline-flex items-center px-1.5 rounded text-[11px] font-medium"
                      style={{
                        background: u.active ? "#DCFCE7" : "#FEE2E2",
                        color: u.active ? "#16A34A" : "#DC2626",
                      }}
                    >
                      {u.active ? "Attivo" : "Disattivato"}
                    </span>
                    {u.must_change_password && (
                      <span
                        className="ml-2 inline-flex items-center px-1.5 rounded text-[11px]"
                        style={{ background: "#FEF3C7", color: "#B45309" }}
                      >
                        pwd da cambiare
                      </span>
                    )}
                  </td>
                  <td
                    className="px-3 py-2.5 text-[12px]"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString("it-IT")
                      : "mai"}
                  </td>
                  <td className="px-3 py-2.5 text-right space-x-1">
                    <button
                      type="button"
                      onClick={() => resetPassword(u)}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded text-[11px]"
                      style={{
                        border: "1px solid var(--border-default)",
                        color: "var(--text-secondary)",
                      }}
                      data-testid={`reset-${u.email}`}
                    >
                      <KeyRound className="h-3 w-3" /> Reset
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleActive(u)}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded text-[11px]"
                      style={{
                        border: "1px solid var(--border-default)",
                        color: u.active ? "#DC2626" : "#16A34A",
                      }}
                      data-testid={`toggle-${u.email}`}
                    >
                      {u.active ? (
                        <UserX className="h-3 w-3" />
                      ) : (
                        <UserCheck className="h-3 w-3" />
                      )}
                      {u.active ? "Disattiva" : "Attiva"}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
